<script lang="ts">
    import type Employees from '@/schemas/public/Employees.ts';
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type {Field, RelatedListButtonType, SectionLinkType, AuditLog} from "@/types/types";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import Debug from "@/components/Debug.svelte";
    import { router, useForm } from '@inertiajs/svelte';
    import InlineEditable from "@/components/Inputs/InlineEditable.svelte";

    import FormBlock from "@/components/Form/FormBlock.svelte";
    import AppLayout from "@/layouts/AppLayout.svelte";
    import { Pencil, Plus, Trash, Car, FileText, Shield, MessageSquare, Pickaxe, History, Mail } from 'lucide-svelte';
    import AuditLogComponent from '@/components/AuditLogComponent.svelte';
    import {Accordion, AccordionContent, AccordionItem, AccordionTrigger,} from "@/components/ui/accordion"
    import SiteTable from "@/components/sites/SiteTable.svelte";
    import EditCustomerModal from "@/components/customers/EditCustomerModal.svelte";
    import {Button} from "@/components/ui/button";
    import CreateRecordModal from "@/components/CreateRecordModal.svelte";
    import CreateEmployeeModal from "@/components/employees/CreateEmployeeModal.svelte";
    import EditEmployeeModal from "@/components/employees/EditEmployeeModal.svelte";
    import DocumentTable from "@/components/documents/DocumentTable.svelte";
    import CreateDocumentModal from "@/components/documents/CreateDocumentModal.svelte";
    import axios from "axios";
    import RelatedVehicleAssignments from "@/components/vehicles/RelatedVehicleAssignments.svelte";
    import {SvelteMap} from "svelte/reactivity";
    import type {Job} from "@/components/scheduler/types";
    import PreviewDocument from "@/components/documents/PreviewDocument.svelte";
    import type {Document} from "@/types/types";
    import RelatedDocuments from "@/components/documents/RelatedDocuments.svelte";
    import Comments from '@/components/Comments.svelte';
    import { page } from '@inertiajs/svelte';
    import type { Comment } from "@/types/types";
    import RelatedHarnesses from '@/components/harnesses/RelatedHarnesses.svelte';
    import CreateHarnessModal from '@/components/harnesses/CreateHarnessModal.svelte';
    import HarnessDetailModal from '@/components/harnesses/HarnessDetailModal.svelte';

    type Props = {
        employee: Employees;
        blocks: (Blocks & { fields: Field[] })[];
        documentBlocks: (Blocks & { fields: Field[] })[];
        harnessBlocks: (Blocks & { fields: Field[] })[];
        harnessInspectionBlocks: (Blocks & { fields: Field[] })[];
        relatedDocuments?: Document[];
        comments: Comment[];
        entityFieldColumn?: string;
        auditLogs?: AuditLog[];
        jobTypeOptions?: { value: string; label: string }[];
    };

    let {employee, blocks, documentBlocks, harnessBlocks, harnessInspectionBlocks, relatedDocuments, comments, entityFieldColumn, auditLogs = [], jobTypeOptions = []}: Props = $props();

    // Get the entity display name using the entityFieldColumn from the module
    const entityDisplayName = entityFieldColumn && (employee as Record<string, any>)[entityFieldColumn]
        ? (employee as Record<string, any>)[entityFieldColumn]
        : 'Employee Details';

    let breadcrumbs = $state([
        {title: 'Employees', href: route('employees.index')},
        {title: entityDisplayName, href: route('employees.show', {employee: employee.id})},
    ]);

    let deleteForm = $state(useForm({}));

    let actionbuttons = $state([
        {
            title: 'Edit Employee',
            href: undefined,
            onClick: openEditEmployeeModal,
            icon: Pencil,
        },
        {
            title: 'Add New Employee',
            href: undefined,
            icon: Plus,
            onClick: openCreateEmployeeModal,
        },
        {
            title: 'Send Welcome Email',
            href: undefined,
            icon: Mail,
            onClick: () => {
                router.post(route('employees.send-welcome-email', { employee: employee.id }));
            },
        },
        {
            title: 'Delete Employee',
            actionType: 'delete',
            href: undefined,
            onClick: () => {
                $deleteForm.delete(route('employees.destroy', {employee: employee.id}));
            },
            icon: Trash,
        }
    ]);

    let showCreateEmployeeModal = $state(false);

    function openCreateEmployeeModal() {
        showCreateEmployeeModal = true;
    }

    let editEmployeeModal = $state(false);

    function openEditEmployeeModal() {
        editEmployeeModal = true;
    }

    let showCreateDocumentModal = $state(false);
    function openCreateDocumentModal() {
        showCreateDocumentModal = true;
    }

    let showCreateHarnessModal = $state(false);
    let refetchHarnesses: (() => Promise<void>) | undefined = $state();
    function openCreateHarnessModal() {
        showCreateHarnessModal = true;
    }

    let showHarnessDetailModal = $state(false);
    let selectedHarness = $state<any>(null);
    function openHarnessDetailModal(harness: any) {
        selectedHarness = harness;
        showHarnessDetailModal = true;
    }

    let inlineFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            inlineFormInner[field.column_name] = field.value;
        }
    }
    let inlineForm = $state(useForm(inlineFormInner));
    let editingFieldId: undefined | number = $state(undefined);

    let relatedColumn = 'related_record_id';
    let related_record_id = employee.id;

    // Section links for quick navigation
    const sectionLinks: SectionLinkType[] = [
        { id: 'section-assignments', label: 'Job Assignments', icon: Pickaxe},
        { id: 'section-documents', label: 'Documents', icon: FileText },
        { id: 'section-harnesses', label: 'Harnesses', icon: Shield },
        { id: 'section-comments', label: 'Comments', icon: MessageSquare },
        { id: 'section-audit-log', label: 'History', icon: History },
    ];

</script>
<!--<Debug {formInner} {form} {$form}/>-->

<svelte:head>
    <title>Employee - {entityDisplayName}</title>
</svelte:head>
<AppLayout {breadcrumbs} {actionbuttons} {sectionLinks}>
    <FormContainer>
        {#each blocks as block}
            <FormBlock label={block.label} collapsible={true}>
                {#each block.fields as field}
                    <InlineEditable
                        field={field}
                        bind:editingFieldId={editingFieldId}
                        error={$inlineForm.errors[field.column_name]}
                        onSave={(newValue) => {
                                $inlineForm[field.column_name] = newValue;
                                $inlineForm.put(route("employees.update", { employee }), {
                                    onSuccess: () => {
                                        editingFieldId = undefined;
                                    },
                                    onError: () => {

                                    }
                                });
                                // editingFieldId = undefined;
                            }
                        }
                        record={employee}
                    />
                {/each}
            </FormBlock>
        {/each}
    </FormContainer>

    <RelatedVehicleAssignments id="section-assignments" column="employee_id" recordId={employee.id} {jobTypeOptions} />

    <RelatedDocuments id="section-documents" {relatedDocuments} blocks={documentBlocks} onOpenCreate={openCreateDocumentModal}>
        <CreateDocumentModal
            blocks={documentBlocks}
            bind:show={showCreateDocumentModal}
            relatedRecordType="App\Models\Employee"
            relatedRecordId={employee.id}
            relatedRecordLabel={employee.name}
        />
    </RelatedDocuments>

    <RelatedHarnesses
        id="section-harnesses"
        employeeId={employee.id}
        blocks={harnessBlocks}
        onOpenCreate={openCreateHarnessModal}
        onRowClick={openHarnessDetailModal}
        bind:refetch={refetchHarnesses}
    >
        <CreateHarnessModal
            blocks={harnessBlocks}
            bind:show={showCreateHarnessModal}
            employeeId={employee.id}
            onSuccess={() => refetchHarnesses?.()}
        />
        <HarnessDetailModal
            harness={selectedHarness}
            bind:show={showHarnessDetailModal}
            inspectionBlocks={harnessInspectionBlocks}
            harnessBlocks={harnessBlocks}
        />
    </RelatedHarnesses>

    <Comments
        id="section-comments"
        commentableId={employee.id}
        commentableType="App\Models\Employee"
        {comments}
        currentUser={$page.props.auth.user}
    />

    <AuditLogComponent id="section-audit-log" {auditLogs} />

    <CreateEmployeeModal
        blocks={blocks}
        bind:show={showCreateEmployeeModal}
    />

    <EditEmployeeModal
        blocks={blocks}
        bind:show={editEmployeeModal}
        employee={employee}
    />



</AppLayout>

